import { queriesEngagements } from "@query";
import {
  WSChip,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSInfiniteScroll,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  RequirementDefinitionStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces/requirement";
import flatten from "lodash/flatten";
import React from "react";
import { Route, Switch, useHistory } from "react-router-dom";

import { Tabs } from "../../../../components/Tabs";
import { WSQueries } from "../../../../query/WSQuery";
import { useQueryRequirementDefinitionList } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinitionList";
import { useQueryRequirementDefinitionListSize } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinitionListSize";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { WS_LINKS } from "../../../../types/wsLinks";
import { openInNewTab } from "../../../../utils/openInNewTab";
import { RequirementDefinitionsTable } from "../../components/RequirementsTable";
import {
  getCreateRequirementDefinitionPathname,
  getSectionPathByRequirementStatus
} from "../../paths/helpers";

import styles from "./styles.module.scss";

const RequirementDefinitionsTab: React.FC<{
  requirementDefinitionStatus?: RequirementDefinitionStatus;
  listPath: string;
  isMainTab?: boolean;
}> = ({ requirementDefinitionStatus, listPath, isMainTab }) => {
  const history = useHistory();

  const requirementsDefinitionsListQuery = useQueryRequirementDefinitionList({
    filter: {
      status: requirementDefinitionStatus,
      requirementType: {
        notIn: [
          RequirementType.Registration,
          RequirementType.PayoutMethod,
          RequirementType.TaxVerification
        ]
      }
    },
    sort: {
      // status: "asc"
      updatedAt: "desc"
    }
  });
  const engagementsListAllQuery =
    queriesEngagements.listEngagements.useQueryAll(undefined, {
      refetchOnMount: false,
      retry: false
    });

  return (
    <WSQueries
      queries={{
        requirementsDefinitionsListQuery,
        engagementsListAllQuery
      }}
    >
      {({ requirementsDefinitionsListQueryData }) => {
        const list = flatten(
          requirementsDefinitionsListQueryData.pages.map(r => r.data) || []
        );
        const listSize =
          requirementsDefinitionsListQueryData.pages[0]?.summary?.listSize || 0;

        return (
          <>
            <WSFlexBox.CenterY className={styles.statsContainer} mb="L">
              <WSElement className={styles.resultsStats}>
                <WSText.ParagraphXs color="gray500">
                  Showing:
                </WSText.ParagraphXs>
                <WSChip
                  pillText={`${listSize} items`}
                  pillType="noBackground"
                  theme="neutral"
                />
              </WSElement>
            </WSFlexBox.CenterY>
            {list.length === 0 ? (
              <WSEmptyState
                actions={
                  isMainTab
                    ? {
                        buttons: [
                          {
                            label: "Create requirement",
                            kind: "Primary",
                            onClick: () => {
                              history.push(
                                getCreateRequirementDefinitionPathname()
                              );
                            }
                          },
                          {
                            label: "Learn about requirements",
                            kind: "Secondary",
                            onClick: () =>
                              openInNewTab(WS_LINKS.eligibilityRequirements)
                          }
                        ]
                      }
                    : undefined
                }
                bgColor="gray"
                description={
                  isMainTab
                    ? "Once you create requirements, they will appear here."
                    : undefined
                }
                hasBorder
                py="3XL"
                title="No requirements found"
                type="documents"
              />
            ) : (
              <WSInfiniteScroll
                endOfList={!requirementsDefinitionsListQuery.hasNextPage}
                loading={!!requirementsDefinitionsListQuery.isFetchingNextPage}
                loadMore={
                  requirementsDefinitionsListQuery.data &&
                  requirementsDefinitionsListQuery.data.pages.length > 0
                }
                onLoad={() => {
                  requirementsDefinitionsListQuery.fetchNextPage();
                }}
              >
                <RequirementDefinitionsTable
                  engagements={engagementsListAllQuery.flatData ?? []}
                  listPath={listPath}
                  requirements={list}
                />
              </WSInfiniteScroll>
            )}
          </>
        );
      }}
    </WSQueries>
  );
};

export const RouteRequirements: React.FC = () => {
  const history = useHistory();
  useBrowserPageTitle("Engagement requirements");

  const queryActiveRequirementDefinitionsSummary =
    useQueryRequirementDefinitionListSize({
      filter: {
        status: RequirementDefinitionStatus.Active,
        requirementType: {
          notIn: [
            RequirementType.Registration,
            RequirementType.PayoutMethod,
            RequirementType.TaxVerification
          ]
        }
      }
    });

  const queryArchivedRequirementDefinitionsSummary =
    useQueryRequirementDefinitionListSize({
      filter: {
        status: RequirementDefinitionStatus.Inactive
      }
    });

  return (
    <WSPage>
      <WSPageToolbar
        actions={[
          {
            label: "Create requirement",
            onClick: async () => {
              history.push(getCreateRequirementDefinitionPathname());
            },
            buttonKind: "Primary"
          }
        ]}
        title={"Engagement requirements"}
      >
        <WSText.ParagraphSm color="gray500" style={{ maxWidth: "550px" }}>
          Engagement requirements can be added to engagements. Contractors must
          complete these requirements to be eligible for payment for those
          engagements.
        </WSText.ParagraphSm>
      </WSPageToolbar>

      <WSQueries
        queries={{
          queryActiveRequirementDefinitionsSummary,
          queryArchivedRequirementDefinitionsSummary
        }}
      >
        {({
          queryActiveRequirementDefinitionsSummaryData,
          queryArchivedRequirementDefinitionsSummaryData
        }) => {
          const tabs = [
            {
              label: "Active",
              path: getSectionPathByRequirementStatus(
                RequirementDefinitionStatus.Active
              ),
              badge: queryActiveRequirementDefinitionsSummaryData,
              requirementDefinitionStatus: RequirementDefinitionStatus.Active,
              isMainTab: true
            },
            {
              label: "Archived",
              path: getSectionPathByRequirementStatus(
                RequirementDefinitionStatus.Inactive
              ),
              badge: queryArchivedRequirementDefinitionsSummaryData,
              requirementDefinitionStatus: RequirementDefinitionStatus.Inactive
            }
          ];

          return (
            <>
              <Tabs mb="L" tabs={tabs} />
              <Switch>
                {tabs.map(tab => (
                  <Route
                    key={tab.path}
                    path={tab.path}
                    render={() => (
                      <RequirementDefinitionsTab
                        isMainTab={tab.isMainTab}
                        listPath={tab.path}
                        requirementDefinitionStatus={
                          tab.requirementDefinitionStatus
                        }
                      />
                    )}
                  />
                ))}
              </Switch>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
